import { Star } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext.jsx';

// Platform-owner tenant switcher (sidebar). Renders only when the logged-in
// user is an owner with more than one tenant available. Selecting a tenant
// re-scopes the whole dashboard (AuthContext.switchTenant persists the choice
// and hard-reloads so every page refetches under the X-Tenant-Id header).
//
// The ★ sets the DEFAULT tenant — the one the console lands on every session.
// Without it the switcher reverted to the login tenant each session (the switch
// only lived in sessionStorage); the default lives in localStorage.
export default function TenantSwitcher() {
  const {
    isOwner,
    availableTenants,
    selectedTenant,
    tenant,
    switchTenant,
    defaultTenantId,
    setDefaultTenant
  } = useAuth();

  // Hide tenants the owner has retired (status 'deleted') — e.g. the redundant
  // Relay Work area — so the switcher only lists live accounts.
  const visibleTenants = (availableTenants || []).filter((t) => t.status !== 'deleted');

  if (!isOwner || visibleTenants.length < 2) return null;

  const current = selectedTenant || tenant;
  const currentIsDefault = !!current?.id && current.id === defaultTenantId;
  // Guard the controlled <select>: if the current tenant isn't a visible option
  // (e.g. a stale default pointing at a since-retired tenant, before the
  // self-heal in AuthContext clears it), fall back to the login tenant's id so
  // the select doesn't render blank.
  const selectValue = visibleTenants.some((t) => t.id === current?.id)
    ? current.id
    : tenant?.id || '';

  return (
    <div className="px-2 mb-3">
      <label
        htmlFor="tenant-switcher"
        className="text-xs uppercase tracking-wider text-stone-500"
      >
        Tenant
      </label>
      <select
        id="tenant-switcher"
        value={selectValue}
        onChange={(e) => {
          const next = visibleTenants.find((t) => t.id === e.target.value);
          if (next && next.id !== current?.id) switchTenant(next);
        }}
        className="mt-1 w-full bg-stone-900 text-stone-100 text-sm rounded-md px-2 py-1.5 border border-stone-700 focus:outline-none focus:border-alfred-gold"
      >
        {visibleTenants.map((t) => (
          <option key={t.id} value={t.id}>
            {(t.companyName || t.subdomain) + (t.id === defaultTenantId ? ' ★' : '')}
          </option>
        ))}
      </select>

      {currentIsDefault ? (
        <div className="mt-1 flex items-center gap-2 text-xs">
          <span className="flex items-center gap-1 text-alfred-gold">
            <Star className="w-3 h-3 fill-current" />
            Default — opens here each session
          </span>
          <button
            type="button"
            onClick={() => setDefaultTenant(null)}
            className="text-stone-500 hover:text-stone-300 underline"
          >
            remove
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setDefaultTenant(current)}
          className="mt-1 flex items-center gap-1 text-xs text-stone-500 hover:text-alfred-gold"
        >
          <Star className="w-3 h-3" />
          Set as default
        </button>
      )}
    </div>
  );
}
